import {
  AddCircleOutline,
  CancelOutlined,
  CheckCircleOutline,
  EmailOutlined,
  ExpandMore,
  LocalShippingOutlined,
  PrintOutlined,
  SaveOutlined,
} from '@mui/icons-material';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  FormHelperText,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { Field, FieldArray, Form, Formik } from 'formik';
import { debounce } from 'lodash';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useNavigate } from 'react-router-dom';

import { fetchCustomerCatalog } from '@sophocles/api/customerCatalog';
import { sendConfirmationEmail } from '@sophocles/api/orderSale';
import { useUser } from '@sophocles/app';
import { AddressDisplayCard, ContactSelector } from '@sophocles/components/contacts';
import { FormikCurrencyTextField } from '@sophocles/components/CurrencyTextField';
import CustomerAutocomplete from '@sophocles/components/CustomerAutocomplete';
import FormErrorAlert from '@sophocles/components/FormErrorAlert';
import ProductAutocomplete from '@sophocles/components/ProductAutocomplete';
import SiteGroupAutocomplete from '@sophocles/components/SiteGroupAutocomplete';
import StatusChip from '@sophocles/components/StatusChip';
import UserAutoComplete from '@sophocles/components/UserAutoComplete';
import { fetchCustomer } from '@sophocles/entities/Customer';
import { fetchSiteGroup } from '@sophocles/entities/SiteGroup';
import { fetchAllUserSellers } from '@sophocles/entities/User';
import { useCreateOrderSaleMutation, usePatchOrderSaleMutation } from '@sophocles/features/entities/orderSales';
import { addToast } from '@sophocles/features/toast';
import { loadShippingTypes } from '@sophocles/features/warehouse/orders/api';
import {
  getOrderSaleCancelDialog,
  getOrderSalePrimaryAction,
} from '@sophocles/features/warehouse/orders/orderSaleActions';
import { buildOrderSalePayload } from '@sophocles/features/warehouse/orders/orderSaleForm';
import { getOrderSaleSchema } from '@sophocles/features/warehouse/orders/orderSaleSchema';
import { shippingTypeSlice } from '@sophocles/features/warehouse/orders/slice';
import { productSlice } from '@sophocles/features/warehouse/products/slice';
import { applyApiErrorToFormik } from '@sophocles/utils/apiErrors';
import { formatDate, formatDateTime } from '@sophocles/utils/formatUtils';
import OrderSaleSummaryTable from '@sophocles/views/sales/components/OrderSaleSummaryTable';
import { FormikShippingTypeSelect } from '@sophocles/views/sales/components/ShippingTypeSelect';
import AddressAutoPopulator from '@sophocles/views/sales/forms/fields/AddressAutoPopulator';
import ContactAutoPopulator from '@sophocles/views/sales/forms/fields/ContactAutoPopulator';
import RadioBillingFields from '@sophocles/views/sales/forms/fields/RadioBillingFields';
import SavePriceButton from '@sophocles/views/sales/forms/fields/SavePriceButton';
import {
  buildContactFromValues,
  isBillingSameAsShipping,
  isDisabledForStatus,
  isNoShippingRequired,
} from '@sophocles/views/sales/forms/orderSaleFields';
import { orderStatusColor } from '@sophocles/views/sales/utils/orderStatus';
import { useOrderSaleAddresses } from '@sophocles/views/sales/utils/useOrderSaleAddresses';
import { useOrderSaleContacts } from '@sophocles/views/sales/utils/useOrderSaleContacts';

function OrderSaleForm({ customerId, readOnly = false, siteGroupId, orderSale, duplicateFrom = null, onClose }) {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const {
    user,
    permissions: { isSalesAdmin, isAccounting, isShipping },
  } = useUser();
  const isSalesAdminOrAccounting = isSalesAdmin || isAccounting;

  const [createOrderSale] = useCreateOrderSaleMutation();
  const [patchOrderSale] = usePatchOrderSaleMutation();

  // Contact picker state management - ContactSelector handles dialog/form state internally
  const { selectedShippingContact, setSelectedShippingContact, selectedBillingContact, setSelectedBillingContact } =
    useOrderSaleContacts({ orderSale, duplicateFrom });
  const [submitAttempted, setSubmitAttempted] = useState(false);
  const [nonFieldMessages, setNonFieldMessages] = useState([]);

  const [showPartialFillWarning, setShowPartialFillWarning] = useState(false);
  const [partialOrderDetails, setPartialOrderDetails] = useState(null);

  const [showCancelOrderDialog, setShowCancelOrderDialog] = useState(false);
  const [showConfirmationEmailDialog, setShowConfirmationEmailDialog] = useState(false);

  const [liveCustomerId, setLiveCustomerId] = useState(
    customerId != null ? Number(customerId) : (orderSale?.customer ?? duplicateFrom?.customer ?? null),
  );
  const [liveSiteGroupId, setLiveSiteGroupId] = useState(
    siteGroupId != null ? Number(siteGroupId) : (orderSale?.site_group ?? duplicateFrom?.site_group ?? null),
  );

  const [customerName, setCustomerName] = useState('-');
  const [siteGroupName, setSiteGroupName] = useState('-');

  // Auto-fill contacts using priority logic (only for new orders)
  const isNewOrder = !orderSale;

  const shippingTypes = useSelector(state => shippingTypeSlice.selectors.selectAllEntities(state));

  const [readOnlySections, setReadOnlySections] = useState({
    general:
      orderSale?.status === 'Complete' ||
      orderSale?.status === 'Cancelled' ||
      orderSale?.status === 'Shipped' ||
      orderSale?.status === 'Deleted' ||
      readOnly,
    items:
      orderSale?.status === 'Complete' ||
      orderSale?.status === 'Cancelled' ||
      orderSale?.status === 'Shipped' ||
      orderSale?.status === 'Deleted' ||
      readOnly,
    shipping:
      orderSale?.status === 'Complete' ||
      orderSale?.status === 'Cancelled' ||
      orderSale?.status === 'Shipped' ||
      orderSale?.status === 'Deleted' ||
      readOnly,
    billing:
      orderSale?.status === 'Complete' ||
      orderSale?.status === 'Cancelled' ||
      orderSale?.status === 'Shipped' ||
      orderSale?.status === 'Deleted' ||
      readOnly,
  });

  const isReadOnly =
    readOnly ||
    (readOnlySections.general && readOnlySections.items && readOnlySections.shipping && readOnlySections.billing);

  const products = useSelector(state => productSlice.selectors.selectAllEntities(state));
  const [customerCatalog, setCustomerCatalog] = useState([]);

  useEffect(() => {
    if (liveCustomerId != null) {
      fetchCustomerCatalog(liveCustomerId).then(data => {
        setCustomerCatalog(data);
      });
    }
  }, [liveCustomerId]);

  // Fetch customer and site group names when loaded with prop-based IDs (for auto-fill)
  useEffect(() => {
    // Only fetch if we have IDs but names are still defaults (not set by autocomplete)
    if (liveCustomerId && customerName === '-') {
      dispatch(fetchCustomer(liveCustomerId)).then(res => {
        if (res && res.name) {
          setCustomerName(res.name);
        } else if (res && res.get) {
          setCustomerName(res.get('name', 'Customer'));
        }
      });
    }
  }, [liveCustomerId, customerName, dispatch]);

  useEffect(() => {
    // Only fetch if we have IDs but names are still defaults (not set by autocomplete)
    if (liveSiteGroupId && siteGroupName === '-') {
      dispatch(fetchSiteGroup(liveSiteGroupId)).then(res => {
        if (res && res.name) {
          setSiteGroupName(res.name);
        } else if (res && res.get) {
          setSiteGroupName(res.get('name', 'Site Group'));
        }
      });
    }
  }, [liveSiteGroupId, siteGroupName, dispatch]);

  const {
    customerPrimaryShipAddress,
    customerPrimaryBillAddress,
    siteGroupPrimaryShipAddress,
    siteGroupPrimaryBillAddress,
  } = useOrderSaleAddresses({ liveCustomerId, liveSiteGroupId });

  const [users, setUsers] = useState([]);
  const getSalesperson = userId => {
    return users.find(u => u.id == userId);
  };

  // Compute initial values with address auto-population (prevents form reinitialization issues)
  const initialValues = React.useMemo(() => {
    if (orderSale) {
      // For existing orders, compute billing_same_as_shipping based on field comparison
      return {
        ...orderSale,
        billing_same_as_shipping: isBillingSameAsShipping(orderSale),
      };
    }

    // Base initial values for new orders — seeded from duplicateFrom when present.
    const blankItem = {
      item: '',
      item_price: 0,
      qty_order: 0,
      qty_fill: 0,
      has_radios: false,
      radio_billing_type: '',
      iccids: [],
      prepaid_service: null,
      prepaid_cost: 0,
      prepaid_follows_radio: false,
      term_length_prepaid: 0,
      term_length_trial: 0,
    };

    let baseValues = {
      id: null,
      customer: liveCustomerId,
      site_group: liveSiteGroupId,
      ship_type: duplicateFrom?.ship_type ?? null,
      ship_company_name: duplicateFrom?.ship_company_name ?? '',
      ship_contact: duplicateFrom?.ship_contact ?? '',
      ship_email: duplicateFrom?.ship_email ?? '',
      ship_phone: duplicateFrom?.ship_phone ?? '',
      ship_address: duplicateFrom?.ship_address ?? '',
      ship_city: duplicateFrom?.ship_city ?? '',
      ship_state: duplicateFrom?.ship_state ?? '',
      ship_zip: duplicateFrom?.ship_zip ?? '',
      ship_country: duplicateFrom?.ship_country ?? '',
      ship_instructions: duplicateFrom?.ship_instructions ?? '',
      ship_auto_save: false,
      bill_contact: duplicateFrom?.bill_contact ?? '',
      bill_company_name: duplicateFrom?.bill_company_name ?? '',
      bill_email: duplicateFrom?.bill_email ?? '',
      bill_phone: duplicateFrom?.bill_phone ?? '',
      bill_address: duplicateFrom?.bill_address ?? '',
      bill_city: duplicateFrom?.bill_city ?? '',
      bill_state: duplicateFrom?.bill_state ?? '',
      bill_zip: duplicateFrom?.bill_zip ?? '',
      bill_country: duplicateFrom?.bill_country ?? '',
      bill_auto_save: false,
      billing_same_as_shipping: false,
      should_notify_customer: false,
      should_hold_billing: false,
      status: 'Draft',
      salesperson: duplicateFrom?.salesperson ?? null,
      date_req_by: null,
      is_invoiced: false,
      po_num: '',
      order_total: 0,
      shipping_cost: 0,
      tracking: '',
      authorization_name: '',
      comments: duplicateFrom?.comments ?? '',
      hidden_comments: '',
      company_name: '',
      priority: false,
      external_warehouse_fulfillment: false,
      order_sale_items: duplicateFrom?.order_sale_items?.length > 0 ? duplicateFrom.order_sale_items : [blankItem],
    };

    // Auto-populate addresses for new orders from the customer's primary saved address.
    // Skip when duplicating — the original order's addresses are carried forward
    // via duplicateFrom and must not be overwritten.
    if (!duplicateFrom) {
      if (siteGroupPrimaryBillAddress) {
        baseValues.bill_address =
          siteGroupPrimaryBillAddress.address_1 +
          (siteGroupPrimaryBillAddress.address_2 ? ', ' + siteGroupPrimaryBillAddress.address_2 : '');
        baseValues.bill_city = siteGroupPrimaryBillAddress.city;
        baseValues.bill_state = siteGroupPrimaryBillAddress.state;
        baseValues.bill_zip = siteGroupPrimaryBillAddress.zipcode;
        baseValues.bill_country = siteGroupPrimaryBillAddress.country;
      } else if (customerPrimaryBillAddress) {
        baseValues.bill_address =
          customerPrimaryBillAddress.address_1 +
          (customerPrimaryBillAddress.address_2 ? ', ' + customerPrimaryBillAddress.address_2 : '');
        baseValues.bill_city = customerPrimaryBillAddress.city;
        baseValues.bill_state = customerPrimaryBillAddress.state;
        baseValues.bill_zip = customerPrimaryBillAddress.zipcode;
        baseValues.bill_country = customerPrimaryBillAddress.country;
      }

      if (siteGroupPrimaryShipAddress) {
        baseValues.ship_address =
          siteGroupPrimaryShipAddress.address_1 +
          (siteGroupPrimaryShipAddress.address_2 ? ', ' + siteGroupPrimaryShipAddress.address_2 : '');
        baseValues.ship_city = siteGroupPrimaryShipAddress.city;
        baseValues.ship_state = siteGroupPrimaryShipAddress.state;
        baseValues.ship_zip = siteGroupPrimaryShipAddress.zipcode;
        baseValues.ship_country = siteGroupPrimaryShipAddress.country;
      } else if (customerPrimaryShipAddress) {
        baseValues.ship_address =
          customerPrimaryShipAddress.address_1 +
          (customerPrimaryShipAddress.address_2 ? ', ' + customerPrimaryShipAddress.address_2 : '');
        baseValues.ship_city = customerPrimaryShipAddress.city;
        baseValues.ship_state = customerPrimaryShipAddress.state;
        baseValues.ship_zip = customerPrimaryShipAddress.zipcode;
        baseValues.ship_country = customerPrimaryShipAddress.country;
      }
    }

    return baseValues;
  }, [
    orderSale,
    duplicateFrom,
    liveCustomerId,
    liveSiteGroupId,
    siteGroupPrimaryBillAddress,
    customerPrimaryBillAddress,
    siteGroupPrimaryShipAddress,
    customerPrimaryShipAddress,
  ]);

  const handleSubmit = async (values, formikHelpers) => {
    try {
      setSubmitAttempted(true);
      setNonFieldMessages([]);

      // Validate contact selection for non-draft orders (combined contact + address)
      const isNoShipping = isNoShippingRequired(shippingTypes, values.ship_type);
      if (!isNoShipping && values.status !== 'Draft') {
        if (!selectedShippingContact) {
          dispatch(
            addToast({
              message: t('warehouse.selectShippingContact', 'Please select a shipping contact'),
              level: 'warning',
            }),
          );
          return;
        }
      }

      if (values.status !== 'Draft' && !values.billing_same_as_shipping) {
        if (!selectedBillingContact) {
          dispatch(
            addToast({
              message: t('warehouse.selectBillingContact', 'Please select a billing contact'),
              level: 'warning',
            }),
          );
          return;
        }
      }

      // Validate contacts are selected with email when "Send Confirmation E-mail" is checked
      if (values.should_notify_customer) {
        // Require shipping contact with email (unless no shipping required)
        if (!isNoShipping) {
          if (!selectedShippingContact) {
            dispatch(
              addToast({
                message: t(
                  'warehouse.selectShippingContactForEmail',
                  'Please select a shipping contact to send confirmation email',
                ),
                level: 'error',
              }),
            );
            return { validationFailed: true };
          }
          const shipEmail = values.ship_email || selectedShippingContact?.email;
          if (!shipEmail) {
            dispatch(
              addToast({
                message: t(
                  'warehouse.shippingContactNeedsEmail',
                  'Shipping contact must have an email to send confirmation email',
                ),
                level: 'error',
              }),
            );
            return { validationFailed: true };
          }
        }

        // Require billing contact with email (use shipping if "Same as Shipping" is checked)
        if (!values.billing_same_as_shipping) {
          if (!selectedBillingContact) {
            dispatch(
              addToast({
                message: t(
                  'warehouse.selectBillingContactForEmail',
                  'Please select a billing contact to send confirmation email',
                ),
                level: 'error',
              }),
            );
            return { validationFailed: true };
          }
          const billEmail = values.bill_email || selectedBillingContact?.email;
          if (!billEmail) {
            dispatch(
              addToast({
                message: t(
                  'warehouse.billingContactNeedsEmail',
                  'Billing contact must have an email to send confirmation email',
                ),
                level: 'error',
              }),
            );
            return { validationFailed: true };
          }
        }
      }

      const payload = buildOrderSalePayload(values, { selectedShippingContact, selectedBillingContact });

      try {
        const data = orderSale
          ? await patchOrderSale({ id: orderSale.id, body: payload }).unwrap()
          : await createOrderSale({ body: payload }).unwrap();
        dispatch(productSlice.actions.setLastLoaded(null));
        return { payload: data };
      } catch (error) {
        const messages = applyApiErrorToFormik(error, {
          setErrors: formikHelpers.setErrors,
          genericMessage: t('warehouse.orderSaveFailed', 'Failed to save order'),
        });
        setNonFieldMessages(messages);
        return { error: true, payload: error?.data };
      }
    } catch (error) {
      const messages = applyApiErrorToFormik(error, {
        setErrors: formikHelpers.setErrors,
        genericMessage: t('warehouse.orderSaveFailed', 'Failed to save order'),
      });
      setNonFieldMessages(messages);
      console.error('Failed to save order', error);
      return { error: true };
    }
  };

  const debounceRef = useRef(
    debounce((qty, index, item, setFieldValue) => {
      if (isNaN(qty)) {
        qty = 0;
      }

      if (item.has_radios) {
        let rawIccids = item.iccids ?? [];
        let iccids = rawIccids.slice(0, qty);

        if (qty > rawIccids.length) {
          for (let i = rawIccids.length; i < qty; i++) {
            iccids.push({ id: null, iccid: '' });
          }
        }

        setFieldValue(`order_sale_items.${index}.iccids`, iccids);
      }
    }, 1000), // 1s debounce
  );

  useEffect(() => {
    dispatch(fetchAllUserSellers()).then(res => setUsers(res));
  }, [dispatch]);

  // Removed useEffect for orderSale - now handled by useMemo above

  useEffect(() => {
    // load if no shipping types
    if (shippingTypes.length === 0) {
      dispatch(loadShippingTypes());
    }
  }, [shippingTypes]);

  // Removed problematic useEffect that caused form reinitialization
  // Address auto-population is now handled via initialValues computation

  /**
   * Handle Shipping contact change from ContactSelector
   * Populates Formik fields for validation and submission
   */
  const handleShippingContactChange = useCallback(
    (contact, setFieldValue) => {
      if (!contact) return;

      setSelectedShippingContact(contact);

      const isAddress = contact._isAddress || contact._isSynthetic || false;

      // Use siteGroup/customer name as fallback for business_name (like RMA pages)
      const businessName = contact.business_name || siteGroupName || customerName || '';

      // Populate Formik fields for validation
      setFieldValue('ship_contact', contact.name || '');
      setFieldValue('ship_company_name', businessName);
      setFieldValue('ship_email', contact.email || '');
      setFieldValue('ship_phone', contact.phone1 || '');
      setFieldValue('ship_address', contact.address || '');
      setFieldValue('ship_city', contact.city || '');
      setFieldValue('ship_state', contact.state || '');
      setFieldValue('ship_zip', contact.zipcode || '');
      setFieldValue('ship_country', contact.country || '');

      // Track contact FK (null if synthetic/address)
      setFieldValue('shipping_contact', isAddress ? null : contact.id);
    },
    [siteGroupName, customerName],
  );

  /**
   * Handle Billing contact change from ContactSelector
   * Populates Formik fields for validation and submission
   */
  const handleBillingContactChange = useCallback(
    (contact, setFieldValue) => {
      if (!contact) return;

      setSelectedBillingContact(contact);

      const isAddress = contact._isAddress || contact._isSynthetic || false;

      // Use siteGroup/customer name as fallback for business_name (like RMA pages)
      const businessName = contact.business_name || siteGroupName || customerName || '';

      // Populate Formik fields for validation
      setFieldValue('bill_contact', contact.name || '');
      setFieldValue('bill_company_name', businessName);
      setFieldValue('bill_email', contact.email || '');
      setFieldValue('bill_phone', contact.phone1 || '');
      setFieldValue('bill_address', contact.address || '');
      setFieldValue('bill_city', contact.city || '');
      setFieldValue('bill_state', contact.state || '');
      setFieldValue('bill_zip', contact.zipcode || '');
      setFieldValue('bill_country', contact.country || '');

      // Track contact FK (null if synthetic/address)
      setFieldValue('billing_contact', isAddress ? null : contact.id);
    },
    [siteGroupName, customerName],
  );

  /**
   * Get aggregated error message for shipping fields to display on ContactSelector
   * Shows errors after submit attempt OR when individual fields are touched
   */
  const getShippingFieldErrors = (errors, touched) => {
    const shippingFields = [
      { key: 'ship_contact', label: t('general.name', 'Name') },
      { key: 'ship_company_name', label: t('warehouse.company', 'Company') },
      { key: 'ship_email', label: t('general.email', 'Email') },
      { key: 'ship_address', label: t('warehouse.address', 'Address') },
      { key: 'ship_city', label: t('warehouse.city', 'City') },
      { key: 'ship_state', label: t('warehouse.state', 'State') },
      { key: 'ship_zip', label: t('warehouse.zip', 'Zip') },
      { key: 'ship_country', label: t('warehouse.country', 'Country') },
    ];

    const fieldErrors = shippingFields
      .filter(field => (submitAttempted || touched[field.key]) && errors[field.key])
      .map(field => `${field.label}: ${errors[field.key]}`);

    return fieldErrors.length > 0 ? fieldErrors.join('; ') : '';
  };

  /**
   * Get aggregated error message for billing fields to display on ContactSelector
   * Shows errors after submit attempt OR when individual fields are touched
   */
  const getBillingFieldErrors = (errors, touched) => {
    const billingFields = [
      { key: 'bill_contact', label: t('general.name', 'Name') },
      { key: 'bill_email', label: t('general.email', 'Email') },
      { key: 'bill_address', label: t('warehouse.address', 'Address') },
      { key: 'bill_city', label: t('warehouse.city', 'City') },
      { key: 'bill_state', label: t('warehouse.state', 'State') },
      { key: 'bill_zip', label: t('warehouse.zip', 'Zip') },
      { key: 'bill_country', label: t('warehouse.country', 'Country') },
    ];

    const fieldErrors = billingFields
      .filter(field => (submitAttempted || touched[field.key]) && errors[field.key])
      .map(field => `${field.label}: ${errors[field.key]}`);

    return fieldErrors.length > 0 ? fieldErrors.join('; ') : '';
  };

  /**
   * Check if any shipping field has an error
   * Returns true after submit attempt OR when individual fields are touched
   */
  const hasShippingFieldErrors = (errors, touched) => {
    const shippingFields = [
      'ship_contact',
      'ship_company_name',
      'ship_email',
      'ship_address',
      'ship_city',
      'ship_state',
      'ship_zip',
      'ship_country',
    ];
    return shippingFields.some(field => (submitAttempted || touched[field]) && errors[field]);
  };

  /**
   * Check if any billing field has an error
   * Returns true after submit attempt OR when individual fields are touched
   */
  const hasBillingFieldErrors = (errors, touched) => {
    const billingFields = [
      'bill_contact',
      'bill_email',
      'bill_address',
      'bill_city',
      'bill_state',
      'bill_zip',
      'bill_country',
    ];
    return billingFields.some(field => (submitAttempted || touched[field]) && errors[field]);
  };

  /**
   * Handle shipping type change - clear shipping contact if "No Shipping Required"
   */
  const handleShippingTypeChange = useCallback(
    (shipTypeId, setFieldValue) => {
      setFieldValue('ship_type', shipTypeId);

      // If "No Shipping Required" is selected, clear shipping contact (combined contact + address)
      const isNoShipping = isNoShippingRequired(shippingTypes, shipTypeId);
      if (isNoShipping) {
        setSelectedShippingContact(null);
        setFieldValue('ship_contact', '');
        setFieldValue('ship_company_name', '');
        setFieldValue('ship_email', '');
        setFieldValue('ship_phone', '');
        setFieldValue('shipping_contact', null);
        setFieldValue('ship_address', '');
        setFieldValue('ship_city', '');
        setFieldValue('ship_state', '');
        setFieldValue('ship_zip', '');
        setFieldValue('ship_country', '');
      }
    },
    [shippingTypes],
  );

  // Removed FormObserver - replaced by ContactPicker pattern with "Same as Shipping" checkbox

  const handleProductChange = useCallback(
    (arrayHelpers, index, product) => {
      if (!product) return;

      let price = product.item_price;

      const salesItem = {
        ...arrayHelpers.form.values.order_sale_items[index],
        item: product.id,
        item_price: price,
        has_radios: product.has_radios,
      };

      if (product.has_radios) {
        let radioBillingType = null;
        let prepaidServiceId = null;
        let prepaidServiceTermLength = null;
        let prepaidPrice = null;

        if (product.default_service != null) {
          radioBillingType = 'Prepaid';
          prepaidServiceId = product.default_service;
          const prepaidService = products.find(p => p.id == product.default_service);
          prepaidServiceTermLength = prepaidService?.billing_term_months ?? 0;
          const customerPrepaidService = customerCatalog.find(cp => cp.product?.id == product.default_service);
          prepaidPrice = customerPrepaidService?.price ?? prepaidService?.item_price ?? 0;

          if (
            radioBillingType == null ||
            prepaidServiceId == null ||
            prepaidServiceTermLength == null ||
            prepaidPrice == null
          ) {
            console.warn('Failed to set radio billing type, prepaid service, term length, or price');
          } else {
            salesItem.prepaid_service = prepaidServiceId;
            salesItem.prepaid_cost = prepaidPrice;
            salesItem.term_length_prepaid = prepaidServiceTermLength;
            salesItem.radio_billing_type = radioBillingType;
          }
        }
      }

      arrayHelpers.replace(index, salesItem);
    },
    [products, customerCatalog],
  );

  const renderOrderItems = (values, touched, errors, handleChange, handleBlur, setFieldValue) => {
    if (readOnlySections.items) {
      return null;
    }
    return (
      <Accordion
        defaultExpanded
        sx={{
          margin: '8px 0px !important',
          '&.Mui-expanded': {
            margin: '8px 0px !important',
          },
        }}
      >
        <AccordionSummary expandIcon={<ExpandMore />}>
          <Stack>
            <Typography variant="h6">{t('warehouse.orderItems')}</Typography>
            {touched.order_sale_items && Boolean(errors.order_sale_items) && (
              <Typography variant="caption" color="error">
                {t('warehouse.required', 'Required')}
              </Typography>
            )}
          </Stack>
        </AccordionSummary>
        <AccordionDetails>
          <FieldArray
            name="order_sale_items"
            render={arrayHelpers => (
              <div>
                {(values?.order_sale_items ?? []).map((item, index) => (
                  <Box key={index} sx={{ mb: 2 }}>
                    <Paper elevation={3} sx={{ padding: 0, marginTop: 2 }}>
                      <Box sx={{ padding: 2 }}>
                        <Grid container spacing={2} alignItems="center">
                          <Grid size={6}>
                            <ProductAutocomplete
                              fullWidth
                              size="small"
                              disabled={
                                !(
                                  values.status === 'Draft' ||
                                  values.status === 'New' ||
                                  values.status === 'Billing - On Hold'
                                )
                              }
                              customerCatalog={customerCatalog}
                              value={item.item}
                              filter={values.status !== 'Complete' ? null : p => !p.obsolete}
                              onChange={opt => {
                                handleProductChange(arrayHelpers, index, opt);
                              }}
                            />
                          </Grid>
                          <Grid size={2}>
                            <Stack direction="row" alignItems="center" spacing={0.5}>
                              <Field
                                name={`order_sale_items.${index}.item_price`}
                                as={FormikCurrencyTextField}
                                label={t('general.price')}
                                required
                                fullWidth
                                size="small"
                                variant="outlined"
                                type="number"
                                error={
                                  touched.order_sale_items?.[index]?.item_price &&
                                  Boolean(errors.order_sale_items?.[index]?.item_price)
                                }
                                helperText={
                                  touched.order_sale_items?.[index]?.item_price &&
                                  errors.order_sale_items?.[index]?.item_price
                                }
                                disabled={isDisabledForStatus(values.status, 'price')}
                              />
                              <SavePriceButton
                                item={item}
                                liveCustomerId={liveCustomerId}
                                customerCatalog={customerCatalog}
                                isSalesAdmin={isSalesAdmin}
                                products={products}
                                onSaved={updatedCatalog => setCustomerCatalog(updatedCatalog)}
                              />
                            </Stack>
                          </Grid>
                          <Grid size={2}>
                            <Field
                              name={`order_sale_items.${index}.qty_order`}
                              as={TextField}
                              label={t('general.quantity')}
                              required
                              fullWidth
                              size="small"
                              variant="outlined"
                              type="number"
                              inputProps={{ step: 1, min: 1 }}
                              onChange={e => {
                                let qty = parseInt(e.target.value);
                                if (isNaN(qty) || qty < 0) {
                                  qty = 0;
                                }
                                debounceRef.current(qty, index, values.order_sale_items[index], setFieldValue);
                                handleChange(e);
                              }}
                              error={
                                touched.order_sale_items?.[index]?.qty_order &&
                                Boolean(errors.order_sale_items?.[index]?.qty_order)
                              }
                              helperText={
                                touched.order_sale_items?.[index]?.qty_order &&
                                errors.order_sale_items?.[index]?.qty_order
                              }
                              disabled={isDisabledForStatus(values.status, 'qty_order')}
                            />
                          </Grid>
                          {values.status !== 'Draft' &&
                            values.status !== 'Cancelled' &&
                            values.status !== 'Deleted' &&
                            !item.has_radios && (
                              <Grid size={2}>
                                <Field
                                  name={`order_sale_items.${index}.qty_fill`}
                                  as={TextField}
                                  label={t('warehouse.filled')}
                                  fullWidth
                                  size="small"
                                  variant="outlined"
                                  type="number"
                                  inputProps={{ step: 1, min: 0 }}
                                  disabled={isDisabledForStatus(values.status, 'qty_fill')}
                                  error={
                                    touched.order_sale_items?.[index]?.qty_fill &&
                                    Boolean(errors.order_sale_items?.[index]?.qty_fill)
                                  }
                                  helperText={
                                    touched.order_sale_items?.[index]?.qty_fill &&
                                    errors.order_sale_items?.[index]?.qty_fill
                                  }
                                />
                              </Grid>
                            )}
                          {(values.status === 'Draft' ||
                            values.status === 'New' ||
                            values.status === 'Billing - On Hold') && (
                            <Grid size={2}>
                              <Button onClick={() => arrayHelpers.remove(index)} color="error">
                                {t('actions.remove', 'Remove')}
                              </Button>
                            </Grid>
                          )}
                        </Grid>
                      </Box>
                      {item.has_radios && (
                        <RadioBillingFields
                          index={index}
                          item={item}
                          products={products}
                          customerCatalog={customerCatalog}
                          values={values}
                          errors={errors}
                          touched={touched}
                          setFieldValue={setFieldValue}
                          isDisabledForStatus={isDisabledForStatus}
                        />
                      )}
                    </Paper>
                  </Box>
                ))}
                {(values.status === 'Draft' || values.status === 'New' || values.status === 'Billing - On Hold') && (
                  <Button
                    type="button"
                    onClick={() =>
                      arrayHelpers.push({
                        item: '',
                        item_price: 0,
                        qty_order: 0,
                        qty_fill: 0,
                        has_radios: false,
                        radio_billing_type: '',
                        iccids: [],
                        prepaid_service: null,
                        prepaid_cost: 0,
                        prepaid_follows_radio: false,
                        term_length_prepaid: 0,
                        term_length_trial: 0,
                      })
                    }
                    variant="contained"
                    color="primary"
                    startIcon={<AddCircleOutline />}
                    sx={{ mt: 2 }}
                  >
                    {t('warehouse.addItem', 'Add Item')}
                  </Button>
                )}
              </div>
            )}
          />
        </AccordionDetails>
      </Accordion>
    );
  };

  const renderOrderSummary = (status, items, values) => {
    const { comments, hidden_comments } = values;
    const selectedSalesperson = getSalesperson(values.salesperson);
    const itemCount = items.length;

    const isNoShipping = isNoShippingRequired(shippingTypes, values.ship_type);

    return (
      <Accordion
        defaultExpanded
        sx={{
          margin: '8px 0px !important',
          '&.Mui-expanded': {
            margin: '8px 0px !important',
          },
        }}
      >
        <AccordionSummary expandIcon={<ExpandMore />}>
          <Typography variant="h6">{t('warehouse.orderSummary')}</Typography>
        </AccordionSummary>
        <AccordionDetails sx={{ p: 2 }}>
          {/* Order Details */}
          {readOnlySections?.general && (
            <Box sx={{ bgcolor: 'grey.100', p: 2, mb: 2 }}>
              <Typography fontWeight="500" variant="h5">
                {t('warehouse.orderDetails')}
              </Typography>
              <Typography>
                <strong>{t('general.customer')}:</strong> {customerName}
              </Typography>
              <Typography>
                <strong>{t('general.siteGroup')}:</strong> {siteGroupName}
              </Typography>
              <Typography>
                <strong>{t('warehouse.salesPerson')}:</strong>{' '}
                {selectedSalesperson ? `${selectedSalesperson.first_name} ${selectedSalesperson.last_name}` : '-'}
              </Typography>
              <Typography>
                <strong>{t('warehouse.orderNumber')}:</strong> {values.id || '-'}
              </Typography>
              <Typography>
                <strong>{t('warehouse.poNumber')}:</strong> {values.po_num || '-'}
              </Typography>
              <Typography>
                <strong>{t('warehouse.orderDate')}:</strong>{' '}
                {values.order_time ? formatDateTime(new Date(values.order_time), user) : '-'}
              </Typography>
              <Typography>
                <strong>{t('warehouse.requestedBy')}:</strong>{' '}
                {values.date_req_by ? formatDate(new Date(values.date_req_by), user) : '-'}
              </Typography>
              {!isNoShipping && (
                <Typography>
                  <strong>{t('warehouse.shipDate')}:</strong>{' '}
                  {values.ship_date ? formatDateTime(new Date(values.ship_date), user) : '-'}
                </Typography>
              )}
            </Box>
          )}

          <Grid container spacing={2} sx={{ mb: 2 }}>
            {readOnlySections?.shipping && !isNoShipping && (
              <Grid size={{ xs: 12, md: 6 }}>
                <Box sx={{ bgcolor: 'grey.100', p: 2 }}>
                  <Stack spacing={1} direction={'row'} justifyContent={'space-between'} sx={{ mb: 1 }}>
                    <Typography fontWeight="500" variant="h6">
                      {t('warehouse.shipping', 'Shipping')}
                    </Typography>
                    <Chip label={shippingTypes[values.ship_type]?.name} size="small" />
                  </Stack>
                  <AddressDisplayCard contact={buildContactFromValues(values, 'shipping')} disabled showAddress />
                  <Typography variant="body2" color="text.secondary">
                    <b>{t('warehouse.tracking')}:</b> {values.tracking || '-'}
                  </Typography>
                </Box>
              </Grid>
            )}
            {readOnlySections?.billing && (
              <Grid size={{ xs: 12, md: 6 }}>
                <Box sx={{ bgcolor: 'grey.100', p: 2 }}>
                  <Typography fontWeight="500" variant="h6" sx={{ mb: 1 }}>
                    {t('general.billing', 'Billing')}
                  </Typography>
                  <AddressDisplayCard contact={buildContactFromValues(values, 'billing')} disabled showAddress />
                  <Typography variant="body2" color="text.secondary">
                    <b>{t('warehouse.invoice')}:</b>{' '}
                    {values.invoice ? <Link to={`/billing/invoices/${values.invoice}`}>{values.invoice}</Link> : '-'}
                  </Typography>
                </Box>
              </Grid>
            )}
          </Grid>
          {readOnlySections?.general && (
            <Grid size={12}>
              {/* Comments Section */}
              <Grid container spacing={2} sx={{ mb: 2 }}>
                <Grid size={{ xs: 12, md: 6 }}>
                  <Box sx={{ bgcolor: 'grey.100', p: 2 }}>
                    <Typography fontWeight="500" variant="h6">
                      {t('general.comments')}
                    </Typography>
                    <Typography>{comments || '-'}</Typography>
                  </Box>
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                  <Box sx={{ bgcolor: 'grey.100', p: 2 }}>
                    <Typography fontWeight="500" variant="h6">
                      {t('warehouse.internalComments', 'Internal Comments')}
                    </Typography>
                    <Typography>{hidden_comments || '-'}</Typography>
                  </Box>
                </Grid>
              </Grid>
            </Grid>
          )}
          <Grid size={12}>
            <Box sx={{ bgcolor: 'grey.100', p: 2 }}>
              <Typography fontWeight="500" variant="h6">
                {t('warehouse.itemDetails', 'Item Details')}
              </Typography>
              {itemCount <= 0 ? (
                <Typography variant="body1" sx={{ px: 4, py: 2 }}>
                  {t('warehouse.noItemsAdded', 'No items have been added to this order.')}
                </Typography>
              ) : (
                <OrderSaleSummaryTable
                  items={items}
                  shippingCost={values?.shipping_cost != null ? parseFloat(values.shipping_cost) : null}
                  showShipped={status !== 'Draft' && status !== 'New'}
                />
              )}
            </Box>
          </Grid>
        </AccordionDetails>
      </Accordion>
    );
  };

  const renderOrderInformation = (values, touched, errors, handleChange, handleBlur, setFieldValue) => {
    return (
      <Accordion
        defaultExpanded
        sx={{
          margin: '8px 0px !important',
          '&.Mui-expanded': {
            margin: '8px 0px !important',
          },
        }}
      >
        <AccordionSummary expandIcon={<ExpandMore />}>
          <Typography variant="h6">{t('warehouse.orderInformation')}</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Grid container spacing={2} sx={{ px: 4 }}>
            <Grid size={12}>
              <Grid container alignItems="center" spacing={2}>
                <Grid size={2}>
                  <Typography fontWeight="500">{t('warehouse.salesPerson')}</Typography>
                </Grid>
                <Grid size={10}>
                  <Field
                    name="salesperson"
                    as={UserAutoComplete}
                    label={t('warehouse.salesPerson', 'Salesperson')}
                    type="string"
                    fullWidth
                    size="small"
                    sx={{ maxWidth: 400 }}
                    InputLabelProps={{
                      shrink: true,
                    }}
                    variant="outlined"
                    users={users}
                    disabled={isDisabledForStatus(values.status, 'salesperson')}
                    error={touched.salesperson && Boolean(errors.salesperson)}
                    helperText={touched.salesperson && errors.salesperson}
                    required
                  />
                </Grid>
              </Grid>
            </Grid>
            <Grid size={12}>
              <Grid container alignItems="center" spacing={2}>
                <Grid size={2}>
                  <Typography fontWeight="500">{t('warehouse.poNumber')}</Typography>
                </Grid>
                <Grid size={10}>
                  <Field
                    name="po_num"
                    as={TextField}
                    label={t('warehouse.poNumber')}
                    fullWidth
                    size="small"
                    variant="outlined"
                    disabled={isDisabledForStatus(values.status, 'po_num')}
                    error={touched.po_num && Boolean(errors.po_num)}
                    helperText={touched.po_num && errors.po_num}
                  />
                </Grid>
              </Grid>
            </Grid>

            <Grid size={12}>
              <Grid container alignItems="center" spacing={2}>
                <Grid size={2}>
                  <Typography fontWeight="500">{t('general.comments')}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    {t('warehouse.visibleToCustomer', '(Visible to Customer)')}
                  </Typography>
                </Grid>
                <Grid size={10}>
                  <Field
                    name="comments"
                    as={TextField}
                    fullWidth
                    size="small"
                    variant="outlined"
                    multiline
                    rows={4}
                    disabled={isDisabledForStatus(values.status, 'comments')}
                    error={touched.comments && Boolean(errors.comments)}
                    helperText={touched.comments && errors.comments}
                  />
                </Grid>
              </Grid>
            </Grid>
            <Grid size={12}>
              <Grid container alignItems="center" spacing={2}>
                <Grid size={2}>
                  <Typography fontWeight="500">{t('warehouse.hiddenComments')}</Typography>
                </Grid>
                <Grid size={10}>
                  <Field
                    name="hidden_comments"
                    as={TextField}
                    fullWidth
                    size="small"
                    variant="outlined"
                    multiline
                    rows={4}
                    disabled={isDisabledForStatus(values.status, 'hidden_comments')}
                    error={touched.hidden_comments && Boolean(errors.hidden_comments)}
                    helperText={touched.hidden_comments && errors.hidden_comments}
                  />
                </Grid>
              </Grid>
            </Grid>
            <Grid size={12}>
              <Grid container alignItems="center" spacing={2}>
                <Grid size={2}>
                  <Typography fontWeight="500">{t('warehouse.dateRequestedBy')}</Typography>
                </Grid>
                <Grid size={10}>
                  <Field
                    name="date_req_by"
                    as={TextField}
                    label={t('warehouse.dateRequestedBy')}
                    type="date"
                    fullWidth
                    size="small"
                    sx={{ maxWidth: 400 }}
                    disabled={isDisabledForStatus(values.status, 'date_req_by')}
                    InputLabelProps={{
                      shrink: true,
                    }}
                    variant="outlined"
                    required
                    error={touched.date_req_by && Boolean(errors.date_req_by)}
                    helperText={touched.date_req_by && errors.date_req_by}
                  />
                </Grid>
              </Grid>
            </Grid>

            <Grid size={12}>
              <FormControlLabel
                control={<Field name="priority" as={Checkbox} color="primary" checked={values.priority} />}
                label={t('warehouse.priorityOrder')}
                disabled={isDisabledForStatus(values.status, 'priority')}
              />
            </Grid>
            <Grid size={12}>
              <FormControlLabel
                control={
                  <Field
                    name="should_hold_billing"
                    as={Checkbox}
                    color="primary"
                    checked={values.should_hold_billing}
                  />
                }
                label={t('warehouse.holdBillingWhileProcessing')}
                disabled={isDisabledForStatus(values.status, 'should_hold_billing')}
              />
            </Grid>
            <Grid size={12}>
              <FormControlLabel
                control={
                  <Field
                    name="external_warehouse_fulfillment"
                    as={Checkbox}
                    color="primary"
                    checked={values.external_warehouse_fulfillment}
                  />
                }
                label={t('warehouse.fulfilledExternally')}
                disabled={isDisabledForStatus(values.status, 'external_warehouse_fulfillment')}
              />
            </Grid>
            <Grid size={12}>
              <FormControlLabel
                control={
                  <Field
                    name="should_notify_customer"
                    as={Checkbox}
                    color="primary"
                    checked={values.should_notify_customer}
                  />
                }
                label={t('warehouse.sendConfirmationEmail')}
                disabled={isDisabledForStatus(values.status, 'should_notify_customer')}
              />
            </Grid>
          </Grid>
        </AccordionDetails>
      </Accordion>
    );
  };

  const renderShippingInformation = (values, touched, errors, handleChange, handleBlur, setFieldValue) => {
    const isNoShipping = isNoShippingRequired(shippingTypes, values.ship_type);

    return (
      <Accordion
        defaultExpanded
        sx={{
          margin: '8px 0px !important',
          '&.Mui-expanded': {
            margin: '8px 0px !important',
          },
        }}
      >
        <AccordionSummary expandIcon={<ExpandMore />}>
          <Typography variant="h6">{t('warehouse.shippingInformation')}</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Grid container spacing={2} sx={{ px: 4 }}>
            {(values.status === 'Shipped' || values.status === 'Billing - On Hold' || values.status === 'Complete') &&
              !isNoShipping && (
                <>
                  <Grid size={12}>
                    <Grid container alignItems="center" spacing={2}>
                      <Grid size={2}>
                        <Typography fontWeight="500">{t('warehouse.shipUser')}</Typography>
                      </Grid>
                      <Grid size={10}>
                        {values.ship_user_detail != null
                          ? `${values.ship_user_detail.first_name} ${values.ship_user_detail.last_name}`
                          : t('general.unknown', 'Unknown')}
                      </Grid>
                    </Grid>
                  </Grid>
                  <Grid size={12}>
                    <Grid container alignItems="center" spacing={2}>
                      <Grid size={2}>
                        <Typography fontWeight="500">{t('warehouse.shipDate')}</Typography>
                      </Grid>
                      <Grid size={10}>
                        {values.ship_date != null
                          ? new Date(values.ship_date).toLocaleDateString()
                          : t('general.unknown', 'Unknown')}
                      </Grid>
                    </Grid>
                  </Grid>
                </>
              )}
            {values.status !== 'Draft' && (
              <>
                <Grid size={12}>
                  <Grid container alignItems="center" spacing={2}>
                    <Grid size={2}>
                      <Typography fontWeight="500">{t('warehouse.shippingType')}</Typography>
                    </Grid>
                    <Grid size={10}>
                      <Field
                        name="ship_type"
                        as={FormikShippingTypeSelect}
                        label={t('warehouse.shippingType')}
                        fullWidth
                        size="small"
                        variant="outlined"
                        value={values.ship_type ?? ''}
                        onChange={e => handleShippingTypeChange(e.target.value, setFieldValue)}
                        error={touched.ship_type && Boolean(errors.ship_type)}
                        helperText={touched.ship_type && errors.ship_type}
                        disabled={isDisabledForStatus(values.status, 'ship_type')}
                        required={values.status !== 'Draft' && values.status !== null}
                      />
                    </Grid>
                  </Grid>
                </Grid>
                {!isNoShipping && (
                  <>
                    <Grid size={12}>
                      <Grid container alignItems="center" spacing={2}>
                        <Grid size={2}>
                          <Typography fontWeight="500">{t('warehouse.shippingCost')}</Typography>
                        </Grid>
                        <Grid size={10}>
                          <Field
                            name="shipping_cost"
                            as={FormikCurrencyTextField}
                            label={t('warehouse.shippingCost')}
                            fullWidth
                            size="small"
                            variant="outlined"
                            sx={{ maxWidth: 500 }}
                            error={touched.shipping_cost && Boolean(errors.shipping_cost)}
                            helperText={touched.shipping_cost && errors.shipping_cost}
                            disabled={isDisabledForStatus(values.status, 'shipping_cost')}
                          />
                        </Grid>
                      </Grid>
                    </Grid>
                    <Grid size={12}>
                      <Grid container alignItems="center" spacing={2}>
                        <Grid size={2}>
                          <Typography fontWeight="500">{t('warehouse.trackingNumber')}</Typography>
                        </Grid>
                        <Grid size={10}>
                          <Field
                            name="tracking"
                            as={TextField}
                            label={t('warehouse.trackingNumber')}
                            fullWidth
                            size="small"
                            variant="outlined"
                            sx={{ maxWidth: 500 }}
                            error={touched.tracking && Boolean(errors.tracking)}
                            helperText={touched.tracking && errors.tracking}
                            disabled={isDisabledForStatus(values.status, 'tracking')}
                          />
                        </Grid>
                      </Grid>
                    </Grid>
                  </>
                )}
              </>
            )}
            {!isNoShipping && (
              <>
                <Grid size={12}>
                  <Grid container alignItems="center" spacing={2}>
                    <Grid size={2}>
                      <Typography fontWeight="500">{t('warehouse.instructions')}</Typography>
                    </Grid>
                    <Grid size={10}>
                      <Field
                        name="ship_instructions"
                        as={TextField}
                        fullWidth
                        sx={{ maxWidth: 600 }}
                        multiline
                        rows={4}
                        size="small"
                        variant="outlined"
                        error={touched.ship_instructions && Boolean(errors.ship_instructions)}
                        helperText={touched.ship_instructions && errors.ship_instructions}
                        disabled={isDisabledForStatus(values.status, 'ship_instructions')}
                      />
                    </Grid>
                  </Grid>
                </Grid>
                {/* Shipping Contact + Address Selection */}
                <Grid size={{ xs: 12, md: 6 }}>
                  <Box sx={{ mb: 2 }}>
                    <ContactSelector
                      label={t('warehouse.shippingAddress')}
                      value={selectedShippingContact}
                      onChange={contact => handleShippingContactChange(contact, setFieldValue)}
                      customerId={values.customer || undefined}
                      siteGroupId={values.site_group || undefined}
                      customerName={customerName}
                      siteGroupName={siteGroupName}
                      addressType="shipping"
                      required={true}
                      error={(submitAttempted && !selectedShippingContact) || hasShippingFieldErrors(errors, touched)}
                      helperText={
                        getShippingFieldErrors(errors, touched) ||
                        (submitAttempted && !selectedShippingContact
                          ? t('warehouse.shippingContactRequired', 'Shipping contact is required')
                          : !values.customer
                            ? t('warehouse.selectCustomerFirst', 'Select a customer first')
                            : '')
                      }
                      disabled={!values.customer || isDisabledForStatus(values.status, 'shipping_address')}
                      requiredFields={
                        values.should_notify_customer ? ['email', 'name', 'address'] : ['name', 'address']
                      }
                      primaryContext="sales"
                      contextLabel={t('warehouse.order', 'Order')}
                    />
                  </Box>
                </Grid>
              </>
            )}
          </Grid>
        </AccordionDetails>
      </Accordion>
    );
  };

  const renderBillingInformation = (values, touched, errors, handleChange, handleBlur, setFieldValue) => {
    const isNoShipping = isNoShippingRequired(shippingTypes, values.ship_type);

    return (
      <Accordion
        defaultExpanded
        sx={{
          margin: '8px 0px !important',
          '&.Mui-expanded': {
            margin: '8px 0px !important',
          },
        }}
      >
        <AccordionSummary expandIcon={<ExpandMore />}>
          <Typography variant="h6">{t('warehouse.billingInformation')}</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Grid container spacing={2} sx={{ px: 4 }}>
            {(values.status === 'Shipped' || values.status === 'Complete') && (
              <>
                <Grid size={12}>
                  <Grid container alignItems="center" spacing={2}>
                    <Grid size={2}>
                      <Typography fontWeight="500">{t('warehouse.invoiceUser')}</Typography>
                    </Grid>
                    <Grid size={10}>
                      {orderSale?.invoice_user_detail != null
                        ? `${orderSale?.invoice_user_detail.first_name} ${orderSale?.invoice_user_detail.last_name}`
                        : t('general.unknown', 'Unknown')}
                    </Grid>
                  </Grid>
                </Grid>
                <Grid size={12}>
                  <Grid container alignItems="center" spacing={2}>
                    <Grid size={2}>
                      <Typography fontWeight="500">{t('warehouse.invoiceDate')}</Typography>
                    </Grid>
                    <Grid size={10}>
                      {orderSale?.invoice_date != null
                        ? new Date(orderSale?.invoice_date).toLocaleDateString()
                        : t('general.unknown', 'Unknown')}
                    </Grid>
                  </Grid>
                </Grid>
                {orderSale?.invoice != null && (
                  <Grid size={12}>
                    <Button
                      variant="contained"
                      onClick={() => {
                        navigate(`/billing/invoices/${orderSale?.invoice}`);
                      }}
                    >
                      {t('warehouse.viewInvoice', 'View Invoice')}
                    </Button>
                  </Grid>
                )}
                <Grid size={12}>
                  <FormControlLabel
                    control={<Field name="is_invoiced" as={Checkbox} color="primary" checked={values.is_invoiced} />}
                    label={t('warehouse.isInvoiced')}
                    disabled={true}
                  />
                </Grid>
              </>
            )}
            {/* Same as Shipping checkbox */}
            {!isNoShipping && (
              <Grid size={12}>
                <FormControlLabel
                  control={
                    <Field
                      name="billing_same_as_shipping"
                      as={Checkbox}
                      color="primary"
                      checked={values.billing_same_as_shipping}
                    />
                  }
                  label={t('warehouse.sameAsShipping')}
                  disabled={
                    !values.customer ||
                    !selectedShippingContact ||
                    isDisabledForStatus(values.status, 'billing_address')
                  }
                />
              </Grid>
            )}

            {/* Billing Contact + Address Selection - hidden when "Same as Shipping" is checked */}
            {!values.billing_same_as_shipping && (
              <Grid size={{ xs: 12, md: 6 }}>
                <Box sx={{ mb: 2 }}>
                  <ContactSelector
                    label={t('warehouse.billingAddress')}
                    value={selectedBillingContact}
                    onChange={contact => handleBillingContactChange(contact, setFieldValue)}
                    customerId={values.customer || undefined}
                    siteGroupId={values.site_group || undefined}
                    customerName={customerName}
                    siteGroupName={siteGroupName}
                    addressType="billing"
                    required={true}
                    error={(submitAttempted && !selectedBillingContact) || hasBillingFieldErrors(errors, touched)}
                    helperText={
                      getBillingFieldErrors(errors, touched) ||
                      (submitAttempted && !selectedBillingContact
                        ? t('warehouse.billingContactRequired', 'Billing contact is required')
                        : !values.customer
                          ? t('warehouse.selectCustomerFirst', 'Select a customer first')
                          : '')
                    }
                    disabled={!values.customer || isDisabledForStatus(values.status, 'billing_address')}
                    requiredFields={values.should_notify_customer ? ['email', 'name', 'address'] : ['name', 'address']}
                    primaryContext="sales"
                    contextLabel={t('warehouse.order', 'Order')}
                  />
                </Box>
              </Grid>
            )}
          </Grid>
        </AccordionDetails>
      </Accordion>
    );
  };

  const checkIccidsAndFillQuantities = values => {
    let anyIccidsEntered = false;
    let anyFillQuantitiesEntered = false;

    if (values.order_sale_items) {
      values.order_sale_items.forEach(item => {
        // Check for non-empty ICCIDs
        if (item.iccids && item.iccids.some(iccidEntry => iccidEntry.iccid && iccidEntry.iccid.trim() !== '')) {
          anyIccidsEntered = true;
        }
        // Check if qty_fill is greater than 0
        if (item.qty_fill > 0) {
          anyFillQuantitiesEntered = true;
        }
      });
    }

    return { anyIccidsEntered, anyFillQuantitiesEntered };
  };

  const sendConfirmationButton = orderSale?.id != null &&
    ['New', 'Shipped', 'Billing - On Hold'].includes(orderSale?.status) && (
      <Button
        variant="outlined"
        color="primary"
        disabled={showConfirmationEmailDialog}
        startIcon={<EmailOutlined />}
        onClick={() => {
          setShowConfirmationEmailDialog(true);
        }}
      >
        {t('warehouse.resendConfirmation', 'Re-Send Confirmation')}
      </Button>
    );

  // Memoize validation schema to prevent recreation on every render
  const validationSchema = useMemo(() => getOrderSaleSchema(shippingTypes), [shippingTypes]);

  return (
    <Formik
      validateOnChange
      validateOnBlur
      enableReinitialize
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={handleSubmit}
    >
      {({
        isSubmitting,
        values,
        touched,
        errors,
        formikHelpers,
        submitForm,
        setTouched,
        validateForm,
        setFieldValue,
        handleChange,
        handleBlur,
      }) => {
        const { anyIccidsEntered, anyFillQuantitiesEntered } = checkIccidsAndFillQuantities(values);
        const cancelDialog = getOrderSaleCancelDialog(values.status);
        const primaryAction = getOrderSalePrimaryAction(values.status);

        // Handle form submission with status
        const handleFormSubmit = async (status, skipCheck = false, redirect = true) => {
          if (!orderSale && status === 'Cancelled') {
            if (redirect) {
              navigate(`/sales/orders/`);
            }
          }

          if (status === 'Draft') {
            try {
              const result = await handleSubmit({ ...values, status }, formikHelpers);
              // Check if validation failed (handleSubmit returns { validationFailed: true } on validation errors)
              if (result?.validationFailed) {
                return;
              }
              // handleSubmit already surfaces backend errors via the FormErrorAlert banner.
              if (result?.error) {
                return;
              }
              dispatch(
                addToast({
                  message: t('warehouse.orderDraftCreated', 'Order Draft Created Successfully'),
                  level: 'success',
                }),
              );
              if (redirect) {
                navigate(`/sales/orders/`); // Redirect after submission
              } else if (result?.payload?.id != null) {
                navigate(`/sales/orders/${result.payload.id}`);
              } else {
                navigate(`/sales/orders/`);
              }
            } catch (error) {
              dispatch(
                addToast({
                  message: t('warehouse.encounteredIssue', 'Encountered issue: {{error}}', { error }),
                  level: 'error',
                }),
              );
              console.error('Error submitting form:', error);
            }
          } else {
            // Validate the form
            const formErrors = await validateForm();
            setTouched(formErrors);

            // Check if there are no errors
            if (Object.keys(formErrors).length === 0) {
              // Set the field value and submit the form
              if (status === 'Shipped' && !skipCheck) {
                // eslint-disable-next-line no-use-before-define -- handleFormSubmit <-> checkForPartialOrder form a submit-time cycle
                checkForPartialOrder(values); // Trigger partial order check before processing
              } else {
                try {
                  const isCreating = orderSale?.id == null;
                  const result = await handleSubmit({ ...values, status }, formikHelpers);
                  // Check if validation failed (handleSubmit returns { validationFailed: true } on validation errors)
                  if (result?.validationFailed) {
                    return;
                  }
                  // handleSubmit already surfaces backend errors via the FormErrorAlert banner.
                  if (result?.error) {
                    return;
                  }
                  dispatch(
                    addToast({
                      message: isCreating
                        ? t('warehouse.orderCreatedSuccess', 'Order Created Successfully')
                        : t('warehouse.orderModifiedSuccess', 'Order Modified Successfully'),
                      level: 'success',
                    }),
                  );
                  if (redirect) {
                    navigate(`/sales/orders/`); // Redirect after submission
                  }
                } catch (error) {
                  console.error('Error submitting form:', error);
                  dispatch(
                    addToast({
                      message: t('warehouse.encounteredIssue', 'Encountered issue: {{error}}', { error }),
                      level: 'error',
                    }),
                  );
                }
              }
            }
          }
        };

        const handleConfirmPartialOrder = () => {
          setShowPartialFillWarning(false);
          handleFormSubmit('Shipped', true); // or other status
        };

        const handleCancelPartialOrder = () => {
          setShowPartialFillWarning(false);
        };

        const handleConfirmCancelOrder = () => {
          setShowCancelOrderDialog(false);
          handleFormSubmit('Cancelled', true);
        };

        const handleCancelCancelOrder = () => {
          setShowCancelOrderDialog(false);
        };

        // Function to trigger modal when partial order is detected
        const checkForPartialOrder = () => {
          const partiallyFilledItems = values.order_sale_items.filter(item => {
            let qtyFill = item.qty_fill;

            // If the item has radios, calculate qty_fill from the number of non-blank ICCIDs
            if (item.has_radios && Array.isArray(item.iccids)) {
              qtyFill = item.iccids.filter(iccidObj => iccidObj.iccid && iccidObj.iccid.trim() !== '').length;
            }

            // Return true if the calculated qtyFill is less than qty_order
            return qtyFill < item.qty_order;
          });

          if (partiallyFilledItems.length > 0) {
            const partialItemsText = partiallyFilledItems
              .map(item => {
                let qtyFill = item.qty_fill;
                if (item.has_radios && Array.isArray(item.iccids)) {
                  qtyFill = item.iccids.filter(iccidObj => iccidObj.iccid && iccidObj.iccid.trim() !== '').length;
                }
                const product = products.find(p => p.id === item.item);
                return `${qtyFill}/${item.qty_order} ${product.item_name}`;
              })
              .join(', ');

            setPartialOrderDetails(partialItemsText);
            setShowPartialFillWarning(true);
          } else {
            handleFormSubmit('Shipped', true); // Proceed with normal submit
          }
        };

        return (
          <>
            <Form style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
              <ContactAutoPopulator
                setSelectedShippingContact={setSelectedShippingContact}
                setSelectedBillingContact={setSelectedBillingContact}
                isNewOrder={isNewOrder}
                liveCustomerId={liveCustomerId}
                liveSiteGroupId={liveSiteGroupId}
                customerName={customerName}
                siteGroupName={siteGroupName}
              />
              <AddressAutoPopulator
                siteGroupPrimaryBillAddress={siteGroupPrimaryBillAddress}
                customerPrimaryBillAddress={customerPrimaryBillAddress}
                siteGroupPrimaryShipAddress={siteGroupPrimaryShipAddress}
                customerPrimaryShipAddress={customerPrimaryShipAddress}
                liveCustomerId={liveCustomerId}
                liveSiteGroupId={liveSiteGroupId}
              />
              <Dialog open={showPartialFillWarning} onClose={handleCancelPartialOrder}>
                <DialogTitle>{t('warehouse.confirmPartialOrder')}</DialogTitle>
                <DialogContent>
                  <Box sx={{ p: 2 }}>
                    <Typography variant="body1">{t('warehouse.partiallyFilledItems')}</Typography>
                    <br />
                    {partialOrderDetails != null && (
                      <ul>
                        {partialOrderDetails.split(',').map((item, index) => (
                          <li key={`partial-${index}`}>{item.trim()}</li>
                        ))}
                      </ul>
                    )}
                    <Typography variant="caption">
                      {t(
                        'warehouse.partialOrderConfirmBody',
                        'Processing this order will result in a new order being created for the remaining items. Do you wish to continue processing this as a partial order?',
                      )}
                    </Typography>
                  </Box>
                </DialogContent>
                <DialogActions>
                  <Button onClick={handleCancelPartialOrder} color="secondary">
                    {t('general.no', 'No')}
                  </Button>
                  <Button onClick={handleConfirmPartialOrder} color="primary">
                    {t('general.yes', 'Yes')}
                  </Button>
                </DialogActions>
              </Dialog>
              <Dialog open={showConfirmationEmailDialog} onClose={() => setShowConfirmationEmailDialog(false)}>
                <DialogTitle>{t('warehouse.sendConfirmationEmail')}</DialogTitle>
                <DialogContent>
                  <Box sx={{ p: 2 }}>
                    <Typography variant="body1">
                      {t(
                        'warehouse.confirmSendEmail',
                        'Are you sure you want to send a confirmation email to the customer?',
                      )}
                    </Typography>
                  </Box>
                </DialogContent>
                <DialogActions>
                  <Button onClick={() => setShowConfirmationEmailDialog(false)} color="secondary">
                    {t('general.no', 'No')}
                  </Button>
                  <Button
                    onClick={() => {
                      setShowConfirmationEmailDialog(false);
                      sendConfirmationEmail(orderSale.id);
                      dispatch(
                        addToast({
                          message: t('warehouse.confirmationEmailSent', 'Confirmation Email Sent'),
                          level: 'success',
                        }),
                      );
                    }}
                    color="primary"
                  >
                    {t('general.yes', 'Yes')}
                  </Button>
                </DialogActions>
              </Dialog>
              <Dialog open={showCancelOrderDialog} onClose={handleCancelCancelOrder}>
                <DialogTitle>{t(cancelDialog.titleI18nKey, cancelDialog.title)}</DialogTitle>
                <DialogContent>
                  <Stack spacing={2} sx={{ py: 'var(--space-2)' }}>
                    <Typography variant="body1">
                      {t(
                        cancelDialog.bodyI18nKey,
                        cancelDialog.isProcessed
                          ? 'This order has already moved through fulfillment or billing. Cancelling it may void related records and move radios back to OutdoorLink inventory.'
                          : 'This will mark the order as Cancelled. Confirm before changing the order status.',
                      )}
                    </Typography>
                    <Box
                      component="ul"
                      sx={{ m: 0, pl: 'var(--space-4)', color: 'text.secondary', '& li + li': { mt: 0.5 } }}
                    >
                      <li>{t('warehouse.cancelEffect1')}</li>
                      {cancelDialog.isProcessed && (
                        <>
                          <li>{t('warehouse.cancelEffect2')}</li>
                          <li>{t('warehouse.cancelEffect3')}</li>
                          <li>{t('warehouse.cancelEffectInventory')}</li>
                          <li>{t('warehouse.cancelEffect4')}</li>
                          <li>{t('warehouse.cancelEffect5')}</li>
                        </>
                      )}
                    </Box>
                  </Stack>
                </DialogContent>
                <DialogActions>
                  <Button onClick={handleCancelCancelOrder} color="secondary">
                    {t('warehouse.keepOrder', 'Keep Order')}
                  </Button>
                  <Button onClick={handleConfirmCancelOrder} color="error" variant="contained">
                    {t('warehouse.confirmCancelOrder', 'Cancel Order')}
                  </Button>
                </DialogActions>
              </Dialog>
              <Box sx={{ p: 'var(--card-padding)', display: 'flex', flexDirection: 'column', gap: 'var(--page-gap)' }}>
                <Stack
                  direction={{ xs: 'column', sm: 'row' }}
                  spacing={2}
                  justifyContent="space-between"
                  alignItems={{ xs: 'flex-start', sm: 'center' }}
                  sx={{ pb: 'var(--space-3)', borderBottom: '1px solid var(--border-default)' }}
                >
                  <Box>
                    <Typography variant="h5" sx={{ fontSize: 'var(--title-size)', fontWeight: 'var(--title-weight)' }}>
                      {orderSale
                        ? t('warehouse.orderSaleTitle', 'Order Sale #{{id}}', { id: orderSale.id })
                        : t('warehouse.createOrderSaleTitle', 'Create Order Sale')}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {orderSale
                        ? t('warehouse.orderSaleSubtitle', 'Review order details and status-specific actions.')
                        : t('warehouse.createOrderSaleSubtitle', 'Select the order scope before adding items.')}
                    </Typography>
                  </Box>
                  <StatusChip label={values.status} color={orderStatusColor(values.status)} />
                </Stack>

                {orderSale?.parent != null && (
                  <Button
                    onClick={() => {
                      navigate(`/sales/orders/${orderSale.parent}`);
                    }}
                  >
                    {t('warehouse.parentOrder', 'Parent')}
                  </Button>
                )}

                <Stack direction="row" spacing={2} sx={{ display: isReadOnly ? 'none' : 'flex' }}>
                  <CustomerAutocomplete
                    fullWidth
                    required
                    name="customer"
                    sx={{ width: '100%' }}
                    value={values.customer}
                    onSelectedChange={selected => setCustomerName(selected.label)}
                    onChange={(e, customerId, opt) => {
                      setLiveCustomerId(customerId);
                      setFieldValue('customer', customerId);
                      setFieldValue('ship_company', opt?.label ?? '');
                    }}
                    disabled={isDisabledForStatus(values.status, 'customer')}
                    error={touched.customer && Boolean(errors.customer)}
                    helperText={touched.customer && errors.customer}
                  />
                  {values.customer && (
                    <SiteGroupAutocomplete
                      fullWidth
                      name="site_group"
                      required
                      customerId={values.customer}
                      sx={{ width: '100%' }}
                      value={values.site_group}
                      onSelectedChange={selected => setSiteGroupName(selected.label)}
                      onChange={(e, siteGroupId, opt) => {
                        if (liveCustomerId != values.customer) {
                          setLiveCustomerId(values.customer);
                        }
                        setLiveSiteGroupId(siteGroupId);
                        setFieldValue('site_group', siteGroupId);
                      }}
                      disabled={isDisabledForStatus(values.status, 'site_group')}
                    />
                  )}
                </Stack>

                {/* Hide form content until customer AND site group are selected (for new orders) */}
                {!isNewOrder || (values.customer && values.site_group) ? (
                  <>
                    {!readOnlySections.items &&
                      renderOrderItems(values, touched, errors, handleChange, handleBlur, setFieldValue)}

                    {renderOrderSummary(
                      values.status,
                      values.order_sale_items.filter(item => item.item != null && item.item != ''),
                      values,
                    )}

                    {!readOnlySections.general &&
                      renderOrderInformation(values, touched, errors, handleChange, handleBlur, setFieldValue)}

                    {!readOnlySections.shipping &&
                      renderShippingInformation(values, touched, errors, handleChange, handleBlur, setFieldValue)}

                    {!readOnlySections.billing &&
                      renderBillingInformation(values, touched, errors, handleChange, handleBlur, setFieldValue)}
                  </>
                ) : (
                  <Box sx={{ py: 4, textAlign: 'center' }}>
                    <Typography variant="body1" color="text.secondary">
                      {t(
                        'warehouse.selectCustomerAndSiteGroup',
                        'Please select a customer and site group to continue.',
                      )}
                    </Typography>
                  </Box>
                )}

                <FormErrorAlert messages={nonFieldMessages} sx={{ mt: 2 }} />

                {/* Hide action buttons until customer AND site group are selected (for new orders) */}
                {(!isNewOrder || (values.customer && values.site_group)) && (
                  <Box
                    sx={{
                      mt: 'var(--page-gap)',
                      pt: 'var(--space-3)',
                      borderTop: '1px solid var(--border-default)',
                      display: 'flex',
                      flexWrap: 'wrap',
                      gap: 'var(--space-2)',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                    }}
                  >
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--space-2)' }}>
                      {orderSale?.id != null && values.status !== 'Draft' && (
                        <Button
                          variant="outlined"
                          startIcon={<PrintOutlined />}
                          onClick={() => window.open(`/sales/orders/${orderSale.id}/print`, '_blank')}
                        >
                          {t('warehouse.printOrder', 'Print Order')}
                        </Button>
                      )}
                      {sendConfirmationButton}
                    </Box>

                    <Box
                      sx={{
                        display: 'flex',
                        flexWrap: 'wrap',
                        gap: 'var(--space-2)',
                        justifyContent: 'flex-end',
                        ml: 'auto',
                      }}
                    >
                      {values.status === 'Draft' && (
                        <>
                          {orderSale?.id != null && (
                            <Button
                              variant="outlined"
                              color="error"
                              startIcon={<CancelOutlined />}
                              onClick={() => setShowCancelOrderDialog(true)}
                            >
                              {t('warehouse.cancelOrder', 'Cancel Order')}
                            </Button>
                          )}
                          <Button
                            variant="contained"
                            color="primary"
                            startIcon={<SaveOutlined />}
                            disabled={isSubmitting}
                            onClick={() => handleFormSubmit('Draft', false, false)}
                          >
                            {t('warehouse.saveDraft', 'Save Draft')}
                          </Button>
                          <Button
                            variant="contained"
                            color="success"
                            startIcon={<CheckCircleOutline />}
                            disabled={isSubmitting}
                            onClick={() => handleFormSubmit('New')}
                          >
                            {t('warehouse.createOrder', 'Create Order')}
                          </Button>
                        </>
                      )}

                      {values.status === 'New' && (
                        <>
                          <Button
                            variant="outlined"
                            color="error"
                            startIcon={<CancelOutlined />}
                            disabled={isSubmitting}
                            onClick={() => setShowCancelOrderDialog(true)}
                          >
                            {t('warehouse.cancelOrder', 'Cancel Order')}
                          </Button>
                          <Button
                            variant="contained"
                            color="primary"
                            startIcon={<SaveOutlined />}
                            disabled={isSubmitting}
                            onClick={() => handleFormSubmit('New', false, false)}
                          >
                            {t('warehouse.saveChanges', 'Save Changes')}
                          </Button>
                          {values.should_hold_billing && (
                            <Button
                              variant="contained"
                              color="success"
                              startIcon={<LocalShippingOutlined />}
                              disabled={isSubmitting}
                              onClick={() => handleFormSubmit('Billing - On Hold')}
                            >
                              {t('warehouse.processAndHoldBilling', 'Process & Hold Billing')}
                            </Button>
                          )}
                          {!values.should_hold_billing && (
                            <Button
                              variant="contained"
                              color="success"
                              startIcon={<LocalShippingOutlined />}
                              disabled={isSubmitting || !(isShipping || isSalesAdmin)}
                              onClick={() => handleFormSubmit('Shipped')}
                            >
                              {t(primaryAction.i18nKey, primaryAction.label)}
                            </Button>
                          )}
                        </>
                      )}

                      {values.status === 'Billing - On Hold' && (
                        <>
                          <Button
                            variant="outlined"
                            color="error"
                            startIcon={<CancelOutlined />}
                            disabled={isSubmitting || !isSalesAdmin}
                            onClick={() => setShowCancelOrderDialog(true)}
                          >
                            {t('warehouse.cancelOrder', 'Cancel Order')}
                          </Button>
                          <Button
                            variant="contained"
                            color="primary"
                            startIcon={<SaveOutlined />}
                            disabled={isSubmitting}
                            onClick={() => handleFormSubmit('Billing - On Hold', false, false)}
                          >
                            {t('warehouse.saveChanges', 'Save Changes')}
                          </Button>
                          {isSalesAdminOrAccounting && (
                            <Button
                              variant="contained"
                              color="success"
                              startIcon={<CheckCircleOutline />}
                              disabled={isSubmitting}
                              onClick={() => handleFormSubmit('Shipped')}
                            >
                              {t('warehouse.readyToBill', 'Ready to Bill')}
                            </Button>
                          )}
                        </>
                      )}

                      {values.status === 'Shipped' && (
                        <>
                          {isSalesAdminOrAccounting && (
                            <Button
                              variant="outlined"
                              color="error"
                              startIcon={<CancelOutlined />}
                              disabled={isSubmitting || !isSalesAdmin}
                              onClick={() => setShowCancelOrderDialog(true)}
                            >
                              {t('warehouse.cancelOrder', 'Cancel Order')}
                            </Button>
                          )}
                          {isReadOnly ? (
                            <Button
                              variant="contained"
                              color="primary"
                              startIcon={<SaveOutlined />}
                              disabled={isSubmitting}
                              onClick={() => {
                                setReadOnlySections({
                                  general: false,
                                  items: false,
                                  shipping: false,
                                  billing: false,
                                });
                              }}
                            >
                              {t('warehouse.editShippedOrder', 'Edit Shipped Order')}
                            </Button>
                          ) : (
                            <Button
                              variant="contained"
                              color="primary"
                              startIcon={<SaveOutlined />}
                              disabled={isSubmitting}
                              onClick={() => {
                                return handleFormSubmit('Shipped', true, false).then(() => {
                                  setReadOnlySections({
                                    general: true,
                                    items: true,
                                    shipping: true,
                                    billing: true,
                                  });
                                });
                              }}
                            >
                              {t('warehouse.saveChanges', 'Save Changes')}
                            </Button>
                          )}
                          <Button
                            variant="contained"
                            color="success"
                            startIcon={<CheckCircleOutline />}
                            disabled={isSubmitting || !(isAccounting || isSalesAdmin)}
                            onClick={() => handleFormSubmit('Complete')}
                          >
                            {t('warehouse.completeBilling', 'Complete Billing')}
                          </Button>
                        </>
                      )}

                      {values.status === 'Complete' && (
                        <>
                          {isReadOnly ? (
                            <Button
                              variant="contained"
                              color="primary"
                              startIcon={<SaveOutlined />}
                              disabled={isSubmitting}
                              onClick={() => {
                                setReadOnlySections({
                                  general: false,
                                  items: false,
                                  shipping: false,
                                  billing: false,
                                });
                              }}
                            >
                              {t('warehouse.editCompletedOrder', 'Edit Completed Order')}
                            </Button>
                          ) : (
                            <Button
                              variant="contained"
                              color="primary"
                              startIcon={<SaveOutlined />}
                              disabled={isSubmitting}
                              onClick={() => {
                                return handleFormSubmit('Complete', true, false).then(() => {
                                  setReadOnlySections({
                                    general: true,
                                    items: true,
                                    shipping: true,
                                    billing: true,
                                  });
                                });
                              }}
                            >
                              {t('warehouse.saveChanges', 'Save Changes')}
                            </Button>
                          )}
                          {isSalesAdminOrAccounting && (
                            <Button
                              variant="outlined"
                              color="error"
                              startIcon={<CancelOutlined />}
                              disabled={isSubmitting}
                              onClick={() => setShowCancelOrderDialog(true)}
                            >
                              {t('warehouse.cancelOrder', 'Cancel Order')}
                            </Button>
                          )}
                        </>
                      )}
                    </Box>
                  </Box>
                )}
              </Box>
            </Form>
          </>
        );
      }}
    </Formik>
  );
}

export default OrderSaleForm;
